import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import VastuSubHeader from '../components/VastuSubHeader';
import PageBackground from '../components/PageBackground';
import toast from 'react-hot-toast';
import './VastuStyles.css'; // Re-use core styles
import './VastuBookingPage.css'; // Add page-specific styles

const VastuBookingPage = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Read the consultant ID from the URL (e.g., /vastu/book?consultantId=123)
  const preselectedConsultantId = searchParams.get('consultantId');

  const [consultants, setConsultants] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [form, setForm] = useState({
    propertyType: 'residential',
    preferredLanguage: 'Hindi',
    country: 'India',
    consultantId: preselectedConsultantId || '', // Auto-fill if present
    consultationTypeId: '',
    servicePlanId: '',
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    Promise.all([apiService.getVastuConsultants(), apiService.getVastuServicePlans()])
      .then(([consultantsResponse, plansResponse]) => {
        const activeConsultants = consultantsResponse.data || [];
        const activePlans = plansResponse.data || [];
        
        setConsultants(activeConsultants);
        setServicePlans(activePlans);

        // Logic to handle default selections
        setForm(current => {
          let updates = { ...current };
          // If a consultant is pre-selected, ensure we clear servicePlanId
          if (preselectedConsultantId) {
            updates.servicePlanId = '';
          } 
          // If no consultant is pre-selected, default to the first Anandmaya service plan
          else if (activePlans.length > 0) {
            updates.servicePlanId = activePlans[0]._id;
          }
          return updates;
        });
      })
      .catch(() => toast.error('Failed to load consultation data.'));
  }, [preselectedConsultantId]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      if (name === 'consultantId') {
        return { ...current, consultantId: value, consultationTypeId: '', servicePlanId: value ? '' : servicePlans[0]?._id || '' };
      }
      return { ...current, [name]: value };
    });
  };

  const handleFileChange = (event) => setFiles(Array.from(event.target.files || []));

  const submitBooking = async (event) => {
    event.preventDefault();
    if (!isLoggedIn) {
      toast.error('Please login through Anandmaya to submit your drawing.');
      return;
    }
    
    // Make sure a fee is selected
    if (form.consultantId && !form.consultationTypeId) {
        toast.error('Please select a Fee Category for the consultant.');
        return;
    }

    setIsSubmitting(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) payload.append(key, value);
      });
      files.forEach((file) => payload.append('floorPlans', file));

      const response = await apiService.createVastuConsultation(payload);
      toast.success(`Booking ${response.data.bookingCode} created successfully!`);
      
      // Redirect to user's dashboard to complete payment
      navigate('/dashboard/vastu-consultations');
    } catch (err) {
      toast.error(err.message || 'Failed to submit consultation request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedConsultant = consultants.find((c) => c._id === form.consultantId);
  const selectedConsultantType = selectedConsultant?.consultationTypes?.find((t) => t._id === form.consultationTypeId);
  const selectedServicePlan = servicePlans.find((p) => p._id === form.servicePlanId);
  const selectedPricing = selectedConsultant ? selectedConsultantType : selectedServicePlan;
  const visibleConsultants = consultants.filter((c) => (c.consultationTypes || []).some((t) => t.isActive));
  
  const formatPrice = (value, currency = 'INR') => (new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(Number(value || 0)));

  const bgUrl = 'https://images.unsplash.com/photo-1558535235-943a5c518b2b?auto=format&fit=crop&w=1920&q=80';

  return (
    <div className="vastu-page">
      <PageBackground imageUrl={bgUrl} />
      <VastuSubHeader />

      <div className="vastu-main-content-wrapper" style={{ marginTop: '2rem' }}>
        <div className="booking-page-layout">
          
          {/* Left Column: The Form */}
          <div className="booking-form-wrapper">
            <div className="section-heading">
              <h2>Request Vastu Consultation</h2>
              <p>Submit your property details and floor plan. Your data is stored securely and shared only with verified experts.</p>
            </div>

            <form id="consultation-request" className="booking-form" onSubmit={submitBooking}>
              <div className="form-row">
                <label>
                  Consultation Focus *
                  <select name="consultationType" value={form.consultationType} onChange={updateField}>
                    <option value="House Plan Review">House Plan Review</option>
                    <option value="Full Vastu Consultation">Full Vastu Consultation</option>
                    <option value="Commercial Property Consultation">Commercial Property</option>
                    <option value="Follow-up Consultation">Follow-up</option>
                  </select>
                </label>
                <label>
                  Expert Consultant *
                  <select name="consultantId" value={form.consultantId || ''} onChange={updateField}>
                    <option value="">Anandmaya Recommended Team</option>
                    {visibleConsultants.map((c) => (
                      <option key={c._id} value={c._id}>{c.displayName}</option>
                    ))}
                  </select>
                </label>
              </div>

              {selectedConsultant ? (
                <label>
                  Consultant Fee Category *
                  <select name="consultationTypeId" value={form.consultationTypeId || ''} onChange={updateField} required className={!form.consultationTypeId ? 'highlight-select' : ''}>
                    <option value="" disabled>-- Select a service --</option>
                    {(selectedConsultant.consultationTypes || []).filter((type) => type.isActive).map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name} - {formatPrice(type.price, type.currency)} / {type.durationMinutes || 45} mins
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  Service Plan *
                  <select name="servicePlanId" value={form.servicePlanId || ''} onChange={updateField} required>
                    <option value="" disabled>-- Select a plan --</option>
                    {servicePlans.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.name} - {formatPrice(plan.price, plan.currency)} / {plan.durationMinutes || 45} mins
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="form-row">
                <label>Contact Number (WhatsApp preferred) *<input name="phone" type="tel" value={form.phone || ''} onChange={updateField} required /></label>
                <label>Property Type *
                  <select name="propertyType" value={form.propertyType} onChange={updateField}>
                    <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="plot">Plot</option><option value="other">Other</option>
                  </select>
                </label>
              </div>

              <div className="form-row">
                <label>City *<input name="city" value={form.city || ''} onChange={updateField} required /></label>
                <label>State *<input name="state" value={form.state || ''} onChange={updateField} required /></label>
              </div>

              <div className="form-row">
                 <label>Facing Direction *<input name="facingDirection" value={form.facingDirection || ''} onChange={updateField} placeholder="e.g., East, North-East" required/></label>
                 <label>Preferred Language *<input name="preferredLanguage" value={form.preferredLanguage} onChange={updateField} required/></label>
              </div>

              <label>Property Address *<textarea name="address" value={form.address || ''} onChange={updateField} rows="2" required/></label>
              <label>Concerns / Questions *<textarea name="concerns" value={form.concerns || ''} onChange={updateField} rows="3" placeholder="Share specific issues, goals, or room placements." required/></label>

              <label>
                Upload House Drawing / Floor Plan *
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleFileChange} required />
                <small>Supported formats: PDF, JPG, PNG. Clear images yield better reviews.</small>
              </label>

              <button type="submit" className="submit-btn" disabled={!selectedPricing || isSubmitting}>
                {isSubmitting ? 'Creating Booking...' : 'Request Consultation'}
              </button>
              {!selectedPricing && <div className="form-status error" style={{marginTop: '10px'}}>Please select a fee category to continue.</div>}
            </form>
          </div>

          {/* Right Column: Pricing & Process */}
          <div className="booking-sidebar">
            <div className="price-preview sticky-sidebar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div className="fee-icon"><i className="fas fa-wallet"></i></div>
                  <div className="fee-details">
                    <span>Total Fee</span>
                    <strong>{selectedPricing ? formatPrice(selectedPricing.price, selectedPricing.currency) : '--'}</strong>
                  </div>
              </div>
              {selectedPricing?.description && <p style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #ccc' }}>{selectedPricing.description}</p>}
            </div>

            <div className="side-card" style={{ marginTop: '1.5rem' }}>
              <p className="eyebrow">Review Path</p>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>How it works</h2>
              <ol className="timeline-list">
                <li><span>01</span><strong>Share details</strong><small>Upload your property layout and concerns.</small></li>
                <li><span>02</span><strong>Secure payment</strong><small>Pay the consultation fee via your dashboard.</small></li>
                <li><span>03</span><strong>Expert review</strong><small>Assigned expert studies the layout deeply.</small></li>
                <li><span>04</span><strong>Live Meeting</strong><small>Get video guidance and practical remedies.</small></li>
              </ol>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default VastuBookingPage;